import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function TermsOfServicePage() {
  const classes = useStyles();
  const seo = useSEO("terms-of-service");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          Terms of Service
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Last updated: February 23, 2026
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          1. Introduction and Acceptance of Terms
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          These Terms and Conditions (these <strong>"Terms"</strong>) govern
          your access to and use of the Sugar Cribs online platform and services
          (collectively, the <strong>"Platform"</strong> or{" "}
          <strong>"Service"</strong>), which are operated by{" "}
          <strong>TOPV1 LLC</strong> (the{" "}
          <strong>"Company," "we," "us,"</strong> or <strong>"our"</strong>
          ). By accessing, browsing, or using the Platform in any manner, you (
          <strong>"User"</strong> or <strong>"you"</strong>) acknowledge that
          you have read, understood, and agree to be bound by these Terms, as
          well as our{" "}
          <a href="/privacy-policy" className={classes.link}>
            Privacy Policy
          </a>{" "}
          and any other policies or guidelines incorporated by reference. If you
          do not agree to any of these Terms,{" "}
          <strong>you must not use or access the Platform.</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>PLEASE READ THESE TERMS CAREFULLY.</strong> They constitute a
          legally binding agreement between you and the Company. By using the
          Platform, you represent that you have the legal capacity to enter into
          this agreement.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          2. Changes to Terms
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          TOPV1 LLC reserves the right, at its sole discretion, to modify or
          update these Terms at any time. If we make material changes, we will
          provide notice by posting the updated Terms on the Platform or through
          other communications.{" "}
          <strong>All changes are effective immediately upon posting.</strong>{" "}
          It is your responsibility to review the Terms periodically for
          updates. Your continued use of the Platform after updated Terms have
          been posted constitutes your acceptance of the revised Terms. If you
          do not agree to any modification of these Terms, you must discontinue
          use of the Service.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          3. Eligibility
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Platform is intended{" "}
          <strong>
            solely for users who are at least eighteen (18) years of age,
          </strong>{" "}
          or the age of majority in your jurisdiction, whichever is higher. By
          accessing or using the Service, you represent and warrant that:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            You are 18 years of age or older, and legally competent to form a
            binding contract with the Company;
          </li>
          <li className={classes.listItem}>
            You are legally permitted to access adult-oriented content in your
            jurisdiction;
          </li>
          <li className={classes.listItem}>
            All information you submit to the Company during account
            registration is truthful and accurate.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          The Service{" "}
          <strong>
            is not available to persons under the age of majority.
          </strong>{" "}
          The Company may require you to verify your age and identity at any
          time. Accounts found to be used by minors or otherwise ineligible
          users will be terminated immediately. You agree that you will not
          allow any minor to access or use your account. The Company has zero
          tolerance for any use of the Platform by underage individuals and will
          report such use or any content involving minors to law enforcement as
          required by law.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Company may require you to verify your age and identity at any
          time.{" "}
          <strong>
            All users and creators are subject to Know Your Customer (KYC)
            checks in compliance with anti-money laundering (AML) and
            counter-terrorist financing (CTF) laws.
          </strong>{" "}
          Accounts found to be used by minors, sanctioned individuals, or
          otherwise ineligible users will be terminated immediately.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          4. Identity Verification & Onboarding
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To protect our community and comply with applicable laws (including
          the U.S. Bank Secrecy Act/AML, counter-terrorist financing
          requirements, sanctions screening, and 18 U.S.C. § 2257), the Company
          operates a mandatory identity verification and onboarding program for
          Creators and, where applicable, for Users who transact or withdraw
          funds on the Platform.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>a. What we require</strong> (may vary by jurisdiction and
          risk):
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Government-issued photo ID, date of birth, and liveness/biometric
            checks (where permitted by law).
          </li>
          <li className={classes.listItem}>
            Full legal name, address, and contact information; payment account
            details and tax information (if applicable).
          </li>
          <li className={classes.listItem}>
            For Creators: model releases/consents and documentation that all
            persons depicted were adults (18+) at the time of creation;{" "}
            <strong>custodian-of-records details</strong> consistent with 18
            U.S.C. § 2257. See our{" "}
            <a href="/compliance-statement" className={classes.link}>
              2257 Compliance Statement
            </a>{" "}
            for more information.
          </li>
          <li className={classes.listItem}>
            Attestations that content is lawful, consensual, and compliant with
            these Terms and our policies.
          </li>
        </Box>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>b. Prohibited persons</strong> (non-exhaustive):
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Anyone under 18 years old or below the age of majority in their
            jurisdiction.
          </li>
          <li className={classes.listItem}>
            Individuals on sanctions/denied-party lists or in embargoed
            jurisdictions, or otherwise restricted by law or by our partners.
          </li>
          <li className={classes.listItem}>
            Persons who fail, refuse, or cannot complete verification, or who
            provide false or misleading information.
          </li>
        </Box>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>c. Ongoing monitoring & re-verification.</strong> We may
          conduct continuous screening, review transactions, request updated
          information, and require re-verification (e.g., upon name changes,
          unusual activity, payout requests, or risk signals). We may place
          temporary holds while reviews are completed.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>d. Enhanced Due Diligence (EDD).</strong> We may apply EDD
          where risk factors are present, including but not limited to high
          chargeback rates, large or rapid volume changes, mismatch between
          identity and payout details, negative media, or requests from payment
          partners or authorities.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>e. Outcomes for non-compliance.</strong> The Company may
          refuse onboarding, limit features, delay or withhold payouts, suspend
          or terminate accounts, reverse Transactions, and notify payment
          partners or competent authorities as required or permitted by law and
          by our agreements.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>f. Data handling.</strong> We process personal information for
          verification and compliance purposes as described in our{" "}
          <a href="/privacy-policy" className={classes.link}>
            Privacy Policy
          </a>
          , including retention for legal, fraud-prevention, and audit needs. We
          use vetted service providers for KYC/AML and security. Your rights and
          choices are explained in the Privacy Policy.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>g. 2257 responsibilities.</strong> The Company is not the
          producer (primary or secondary) of user-submitted content as defined
          by 18 U.S.C. § 2257. Creators are solely responsible for maintaining
          required records and providing custodian information upon lawful
          request. See the Platform's{" "}
          <a href="/compliance-statement" className={classes.link}>
            2257 Compliance Statement
          </a>{" "}
          for additional detail.
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>h. Questions or appeals.</strong> If you believe a
          verification decision is in error, contact our Legal Compliance Team
          at{" "}
          <a href="mailto:legal@sugarcribs.com" className={classes.link}>
            legal@sugarcribs.com
          </a>{" "}
          with your account email, the decision notice, and any supporting
          documents. We will review in a reasonable timeframe.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          5. User Accounts and Security
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To access certain features of the Platform, you may be required to
          create a user account. When creating an account, you agree to provide
          current, complete, and accurate information as requested, including a
          valid email address and any required identity verification documents.
          You further agree to update your account information promptly if it
          changes, so that it remains accurate and complete.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Account Security:</strong> You are responsible for maintaining
          the confidentiality of your account login credentials and for all
          activities that occur under your account.{" "}
          <strong>You agree to notify the Company immediately</strong> of any
          unauthorized access to or use of your account or any other breach of
          security. TOPV1 LLC will not be liable for any loss or damage arising
          from your failure to safeguard your account credentials. The Company
          reserves the right to disable or suspend any account at any time if,
          in our opinion, you have violated any provision of these Terms or if
          security or legal concerns arise.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          By creating an account, you agree that:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            You will complete any required identity verification (including
            submission of government-issued ID and biometric verification where
            applicable).
          </li>
          <li className={classes.listItem}>
            You will not create accounts on behalf of others, or attempt to
            conceal your true identity, origin of funds, or purpose of
            transactions.
          </li>
          <li className={classes.listItem}>
            <strong>
              All payment activity is subject to monitoring, review, and
              reporting under AML/CTF laws.
            </strong>
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          6. User Conduct and Prohibited Uses
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          By using the Platform, you agree{" "}
          <strong>
            to use the Service only for lawful purposes and in accordance with
            these Terms.
          </strong>{" "}
          You are solely responsible for your conduct while accessing or using
          our Platform. You agree that you <strong>shall not</strong> engage in
          any of the following prohibited activities:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>Illegal or Harmful Acts:</strong> Using the Platform for any
            purpose that violates any applicable law or regulation, or in a
            manner that is fraudulent, harmful, deceptive, threatening,
            harassing, defamatory, or obscene. This includes, without
            limitation, attempting to exploit or harm anyone (including minors)
            in any way and using the Service to share or disseminate any illegal
            content or engage in any unlawful activities.
          </li>
          <li className={classes.listItem}>
            <strong>Unauthorized Access:</strong> Attempting to gain
            unauthorized access to the Platform, other users' accounts, or any
            systems or networks connected to the Platform (for example, by
            hacking, password mining, or any other illegitimate means). You
            agree not to circumvent, disable, or otherwise interfere with any
            security-related features of the Service.
          </li>
          <li className={classes.listItem}>
            <strong>Interference with Service:</strong> Interfering with or
            disrupting the integrity or proper functioning of the Platform. This
            includes transmitting any worms, viruses, malware, or any code of a
            destructive nature, as well as engaging in any denial-of-service
            attacks or similar interference.
          </li>
          <li className={classes.listItem}>
            <strong>Impersonation and Misrepresentation:</strong> Impersonating
            or attempting to impersonate the Company, a Company employee,
            another user, or any other person or entity, or falsely stating or
            otherwise misrepresenting your affiliation with any person or entity
            in connection with the Platform.
          </li>
          <li className={classes.listItem}>
            <strong>Data Mining:</strong> Using any automated system (including
            robots, spiders, or offline readers) to access the Service in a
            manner that sends more request messages to our servers than a human
            can reasonably produce in the same period by using a conventional
            web browser, <strong>except</strong> as may be expressly permitted
            by the Company. Scraping, crawling, or using any other automated
            means to extract data from the Platform without our prior written
            permission is strictly prohibited.
          </li>
          <li className={classes.listItem}>
            <strong>Violation of Others' Rights:</strong> Posting or
            transmitting any content that infringes or violates the intellectual
            property rights, privacy rights, publicity rights, or any other
            rights of any third party. You must not use the Platform to
            distribute unauthorized advertisements or spam (such as junk mail,
            chain letters, pyramid schemes, or other forms of unsolicited
            messages).
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          In addition, you agree that you shall not:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Use the Platform to conceal or disguise the origin of funds, conduct
            fraudulent transactions, launder money, or finance unlawful
            activity.
          </li>
          <li className={classes.listItem}>
            Engage in any tipping, credit transfers, or payments designed to
            artificially inflate earnings or circulate funds without legitimate
            content exchange ("layering" or "round-tripping").
          </li>
          <li className={classes.listItem}>
            Attempt to bypass the Company's KYC, AML, or fraud-prevention
            systems.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          The above list of prohibitions provides examples and is not
          exhaustive. Any conduct that the Company, in its sole discretion,
          believes violates the letter or spirit of these Terms or any
          applicable law may result in suspension or termination of your account
          and access to the Service, and could lead to legal action. We reserve
          the right (but assume no obligation) to investigate and take
          appropriate action against anyone who, in the Company's sole judgment,
          violates these Terms or is otherwise engaging in unlawful or
          objectionable activity on or through the Platform.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          7. Prohibition of Soliciting Sexual Services
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The solicitation, promotion, or exchange of sexual services for money
          or any form of compensation is strictly prohibited on the Platform.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Users are expressly forbidden from:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Offering or requesting sexual favors in exchange for money, gifts,
            or material benefits;
          </li>
          <li className={classes.listItem}>
            Advertising or implying the provision of escort or sex-related
            services;
          </li>
          <li className={classes.listItem}>
            Listing rates, allowances, or compensation in exchange for intimacy
            or sexual activity;
          </li>
          <li className={classes.listItem}>
            Using coded language or indirect expressions to imply such
            exchanges.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Any User Content, message, or interaction that violates this policy
          will be removed, and the associated account may be suspended or
          terminated immediately, without prior notice or refund. The Company
          may also report violations to law enforcement where required by
          applicable law.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          This policy exists to maintain the Platform's integrity, protect our
          community, and comply with local, national, and international laws,
          including the prohibition of prostitution and sex trafficking.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          8. User-Generated Content and Content Standards
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>a. Content Ownership:</strong> The Platform may allow users
          (including content creators) to create, post, upload, or transmit
          content such as text, images, audio, video, livestreams, or other
          materials (collectively, <strong>"User Content"</strong>
          ). You retain any ownership rights that you have in the User Content
          you create or provide. However, by submitting or posting User Content
          on the Platform, you grant <strong>TOPV1 LLC</strong> a worldwide,
          non-exclusive, royalty-free, sublicensable, and transferable license
          to use, reproduce, adapt, modify, publish, translate, create
          derivative works from, distribute, perform, and display your User
          Content (in whole or in part){" "}
          <strong>
            solely for the purpose of operating, marketing, and improving the
            Platform
          </strong>{" "}
          and providing the Service to you and other users. This license will
          remain in effect until such User Content is removed from the Platform,
          and for a reasonable time thereafter for backup and legal archival
          purposes.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>b. Your Responsibilities and Warranties:</strong> You are
          solely responsible for all User Content that you submit or make
          available via the Platform. By posting User Content, you represent and
          warrant that: (i) you own or control all necessary rights in and to
          the content, and have the right to grant the license above; (ii) the
          User Content is lawful, accurate, and not misleading; (iii) the User
          Content does not and will not infringe, misappropriate, or violate any
          third-party rights (including intellectual property rights and rights
          of privacy or publicity); and (iv) the User Content complies with
          these Terms and all applicable laws and regulations.{" "}
          <strong>
            You agree that you will maintain any required written documentation
            or releases as proof that all individuals depicted in your User
            Content (if any) are adults (18 years or older) at the time the
            content was created
          </strong>{" "}
          and have given appropriate consent, in compliance with 18 U.S.C. §
          2257 and other applicable laws. Upon the Company's request, you shall
          promptly provide copies of such documentation.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>c. Content Standards and Prohibited Content:</strong> All User
          Content must adhere to the following content standards.{" "}
          <strong>Prohibited content</strong> includes, but is not limited to,
          any content that:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>Involves Illegal Material:</strong> Contains any material
            that is illegal to possess, produce, or distribute, including but
            not limited to child pornography or content that in any way sexually
            exploits or endangers minors (such content will be reported to law
            enforcement immediately).
          </li>
          <li className={classes.listItem}>
            <strong>Violates Others' Rights:</strong> Infringes any copyright,
            trademark, trade secret, patent, or other intellectual property
            right of any party; or violates any privacy, publicity, or other
            personal rights of others.
          </li>
          <li className={classes.listItem}>
            <strong>Is Hateful or Harassing:</strong> Promotes or depicts hate
            speech, harassment, bullying, or any discrimination based on race,
            ethnicity, national origin, religion, sex, gender, sexual
            orientation, disability, or any other legally protected
            characteristic.
          </li>
          <li className={classes.listItem}>
            <strong>Is Defamatory or Obscene:</strong> Is defamatory, obscene,
            libelous, slanderous, or pornographic beyond what is permitted.
            (Adult-oriented erotic content is allowed on the Platform{" "}
            <strong>to the extent it is legal and consensual,</strong> but any
            obscene material that violates decency laws or content involving
            non-consensual acts is strictly prohibited.)
          </li>
          <li className={classes.listItem}>
            <strong>Promotes Self-Harm or Violence:</strong> Encourages or
            depicts self-harm, suicide, violence, or the violent extremist
            ideology, or provides instructions for the manufacturing of weapons
            or illegal activities.
          </li>
          <li className={classes.listItem}>
            <strong>Contains Harmful Code:</strong> Contains viruses, trojans,
            worms, logic bombs, or other material that is malicious or
            technologically harmful.
          </li>
          <li className={classes.listItem}>
            <strong>Is Fraudulent or Misleading:</strong> Intends to defraud,
            scam, phish, or mislead other users, including impersonating any
            person or entity, or misrepresenting your identity or affiliation.
          </li>
        </Box>

        <Typography variant="body1" className={classes.paragraph}>
          To maintain a safe and compliant environment,{" "}
          <strong>
            all user-uploaded content is subject to mandatory review prior to
            publication.
          </strong>{" "}
          This pre-screening process is designed to ensure that content does not
          violate applicable laws, infringe intellectual property rights, or
          breach the Company's content standards and policies. The Company{" "}
          <strong>reserves the right, but is not obligated,</strong> to reject,
          modify, or remove any content submitted by users if, in our sole
          discretion, it is found to be illegal, harmful, objectionable, or in
          violation of these Terms and Conditions.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Despite our efforts to screen content before publication, you
          acknowledge and agree that you may still encounter material that you
          find offensive or inappropriate. The Company makes no guarantees
          regarding the accuracy, integrity, legality, or quality of user
          content and disclaims any liability related to such content.{" "}
          <strong>
            If you encounter any content on the Platform that you believe
            violates these Terms, please report it to us through the designated
            reporting mechanism or contact us directly.
          </strong>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          9. Fees and Payments
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>a. Purchasing Content, Credits, or Services:</strong> The
          Platform may enable users (<strong>"Fans"</strong> or general users)
          to purchase digital content, subscriptions, credits, services, or
          other offerings from content creators (<strong>"Creators"</strong>) or
          from the Company (collectively, <strong>"Transactions"</strong>).
          Credits may be used to access certain features such as direct messages
          (<strong>"DM Credits"</strong>), and in future, other digital services
          as announced by the Company. By making a purchase, you agree to pay
          all applicable fees and charges associated with that Transaction,
          including any taxes or processing fees. All prices will be displayed
          in U.S. dollars unless otherwise stated and must be paid through our
          approved payment methods. You{" "}
          <strong>authorize the Company (or our payment processors)</strong> to
          charge your payment method for the total amount due.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>b. Free Credits:</strong> New Viewer accounts may receive an
          allocation of free DM Credits upon account creation. Free credits do
          not renew, are non-transferable, and expire after thirty (30) days if
          unused.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>c. Paid Credit Purchases:</strong> Users may purchase credits
          on a one-time ("a la carte") basis or as part of a recurring
          subscription plan. Credit packages, prices, and the amount of credits
          per package will be displayed prior to purchase. All credit sales are
          final and non-refundable except where required by applicable law.
          Credits have no cash value, are non-transferable, and may only be used
          within the Platform as permitted by the Company.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>d. Subscriptions and Credit Rollover:</strong> If you purchase
          a monthly subscription plan that includes credits:
        </Typography>
        <Box component="ol" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Subscription credits are issued at the start of each billing cycle
            and may roll over for up to two (2) consecutive billing cycles
            (approximately sixty (60) days).
          </li>
          <li className={classes.listItem}>
            A rollover balance may not exceed the equivalent of one month's
            credit allotment for your plan. Any credits above this cap will
            expire.
          </li>
          <li className={classes.listItem}>
            Rollover credits are applied before current-month credits when used.
          </li>
          <li className={classes.listItem}>
            Credits that exceed the rollover time limit or cap will expire and
            be removed from your account.
          </li>
          <li className={classes.listItem}>
            The Company reserves the right to modify rollover limits, caps, or
            expiration periods with reasonable notice.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>e. No Cash Redemption:</strong> Credits, including DM Credits,
          are virtual items with no monetary value outside the Platform and may
          not be redeemed for cash, refunded, or transferred, except as required
          by law or expressly permitted by the Company.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>f. No Refunds Policy:</strong> All sales and transactions on
          the Platform are final. Due to the nature of digital content and
          services, the Company maintains a no-refunds policy, unless a refund
          is expressly authorized by us in our sole discretion or required by
          applicable law.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>g. Creator Earnings and Commissions:</strong> If you are a
          content creator authorized by the Company to monetize content on the
          Platform, your earnings from fans (such as subscription fees, tips, or
          payments for content) will be credited to your account, minus any
          platform fees, commissions, chargebacks, or other deductions as
          specified by the Company's policies. The Company typically retains a
          portion of Creator earnings as a service fee for facilitating the
          Platform. The current commission rate or platform fee will be
          communicated in the Creator documentation or onboarding materials (for
          example, if the Company retains twenty percent (20%) and remits eighty
          percent (80%) to the Creator, such terms will be specified in the
          Creator payment terms or guidelines).{" "}
          <strong>
            The Company reserves the right to change its fee structure or
            commission rates
          </strong>{" "}
          upon reasonable notice to Creators (such as by updating our Creator
          terms or sending a notification). Creators are responsible for any
          taxes on their earnings as required by law, and the Company may deduct
          or withhold taxes if required by applicable laws.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>h. Payment Processing and Payouts:</strong> Payments on the
          Platform may be processed by third-party payment processors. By
          conducting Transactions on the Platform, you agree to comply with such
          processors' terms and conditions and privacy policies in addition to
          these Terms. For Creators, payouts of earned balances will be
          disbursed in accordance with our payout schedule and methods (e.g.,
          monthly or as otherwise stated in the Creator terms), subject to
          meeting any minimum payout thresholds. The Company may delay or
          withhold payout of funds if we suspect, in good faith, that a
          transaction is fraudulent, illegal, or in violation of these Terms, or
          if additional verification of your identity or compliance with our
          requirements is needed. We reserve the right to reverse or adjust any
          Transaction (including deducting earnings from a Creator's balance) in
          cases of chargebacks, refunds, fraud, or error.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          All transactions are screened by automated and manual systems to
          detect fraud, chargeback abuse, money laundering, and other prohibited
          conduct.{" "}
          <strong>
            The Company reserves the right to delay, withhold, or reverse
            payouts if transactions are flagged as suspicious or require
            enhanced due diligence.
          </strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You acknowledge that:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            The Company may request additional information, including proof of
            identity, proof of address, and source of funds, before processing
            payouts.
          </li>
          <li className={classes.listItem}>
            The Company may suspend or terminate accounts suspected of AML/CTF
            or fraud violations and report activity to relevant financial
            institutions, regulators, or law enforcement.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>i. Subscription Renewals:</strong> If the Platform offers
          subscription services (such as recurring fan subscriptions to a
          Creator's content), such subscriptions will{" "}
          <strong>automatically renew</strong> at the end of each billing period
          (e.g., monthly), and your chosen payment method will be charged the
          then-current subscription fee,{" "}
          <strong>unless and until you cancel</strong>{" "}
          the subscription prior to the renewal date. You can cancel a
          subscription at any time through your account settings; the
          cancellation will take effect at the end of the current billing
          period, and you will continue to have access to the subscribed content
          until that period expires.
          <strong>No prorated refunds</strong> will be provided for mid-period
          cancellations, except where required by law. All pricing for
          subscriptions, one-time purchases, and tipping options is clearly
          displayed prior to confirmation of each transaction. Users are
          responsible for reviewing and accepting these prices before proceeding
          with payment.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>j. Unauthorized or Fraudulent Charges:</strong> You must
          notify us immediately if you suspect any unauthorized or fraudulent
          use of your payment information or account. We reserve the right to
          cancel or suspend any Transaction or your access to paid services if
          we suspect fraud, unauthorized activity, or violation of these Terms.
          The Company is not liable for any unauthorized use of your payment
          instrument by a third party; however, we will use commercially
          reasonable efforts to work with you (and law enforcement, if
          appropriate) to address such issues.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          10. Intellectual Property Rights
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>a. Ownership of Platform Content:</strong> All content,
          materials, and features provided by the Company on or through the
          Platform – including but not limited to the Sugar Cribs name and logo,
          design elements, artwork, graphics, photographs, videos (other than
          User Content), audio, text, software, and the compilation and
          arrangement of such materials – are the property of{" "}
          <strong>TOPV1 LLC</strong> or its licensors and are protected by
          United States and international copyright, trademark, patent, trade
          secret, and other intellectual property or proprietary rights laws.{" "}
          <strong>All rights are reserved.</strong> You acknowledge that the
          Company and its licensors exclusively own all right, title, and
          interest in and to the Platform and its content (excluding User
          Content provided by users), including any and all intellectual
          property rights.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>b. Limited License to Users:</strong> Subject to your
          compliance with these Terms, the Company grants you a{" "}
          <strong>
            limited, non-exclusive, non-transferable, non-sublicensable,
            revocable license
          </strong>{" "}
          to access and use the Platform and its content for your personal,
          non-commercial use <strong>only.</strong> This license is provided
          solely to allow you to participate in the Platform as intended by
          these Terms. <strong>You must not:</strong>
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Copy, reproduce, distribute, publish, broadcast, publicly display,
            or create derivative works of any content obtained from the Platform
            (other than your own User Content), without the Company's prior
            written consent;
          </li>
          <li className={classes.listItem}>
            Modify, translate, reverse engineer, decompile, disassemble, or
            attempt to derive any source code from the Platform or any software
            used on the Platform;
          </li>
          <li className={classes.listItem}>
            Remove or alter any copyright, trademark, or other proprietary
            rights notices from content on the Platform;
          </li>
          <li className={classes.listItem}>
            Use any of the Company's intellectual property (including the Sugar
            Cribs name or logos) in any manner that suggests endorsement or
            association without our prior written permission.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Any use of the Platform or content therein other than as expressly
          authorized by these Terms is strictly prohibited and will terminate
          the license granted herein. No rights or licenses are granted to you
          by implication or otherwise except for the limited rights expressly
          granted in this section.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>c. Trademarks:</strong> The trademarks, service marks, logos,
          and trade names displayed on the Platform (including "Sugar Cribs" and
          any related logos) are registered and unregistered trademarks of TOPV1
          LLC or its affiliates and licensors. You may not use any Company
          trademarks without our prior written permission. All other trademarks,
          logos, or trade names appearing on the Platform that are not owned by
          the Company are the property of their respective owners and may not be
          used without their consent.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          11. Third-Party Links and Services
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Platform may contain links to or integration with third-party
          websites, content, or services that are not owned or controlled by
          TOPV1 LLC (for example, links to a third-party payment processor,
          identity verification service, or external websites of content
          creators). These links and integrations are provided for your
          convenience only.{" "}
          <strong>
            The Company does not endorse and is not responsible or liable for
            any third-party websites or services,
          </strong>{" "}
          including their content, accuracy, privacy practices, or terms of use.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you access any third-party website or service via links from our
          Platform, you do so <strong>at your own risk.</strong> You understand
          that these Terms and our Privacy Policy do not apply to your use of
          such external sites or resources. We encourage you to review the
          applicable terms and policies of any third-party services you utilize.
          The Company will not be a party to or in any way monitor any
          transaction between you and third-party providers of products or
          services.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          12. Termination and Suspension
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>a. By the Company:</strong> TOPV1 LLC may, in its sole
          discretion and without liability to you, suspend, disable, terminate,
          or block your access to the Platform (or any portion thereof) at any
          time, with or without prior notice, for any reason or no reason at
          all. This includes, without limitation, the Company's right to
          terminate or suspend your account if we believe, in our sole judgment,
          that you have violated these Terms, infringed the rights of others, or
          engaged in any activity that is unlawful or potentially harmful to the
          Company, the Platform, or other users. We may also remove or disable
          access to any User Content you have contributed if it violates these
          Terms or for any other lawful reason. In the event of termination by
          us due to your breach of these Terms or violation of law,{" "}
          <strong>
            you will not be entitled to any refunds or compensation
          </strong>{" "}
          for any unused subscriptions, virtual items, or other services.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>b. By You:</strong> You may terminate your account at any time
          and for any reason by following the account cancellation procedures
          available on the Platform or by contacting us with a request to delete
          your account. Upon termination of your account, your right to access
          and use the Platform will immediately cease. You will remain
          responsible for any fees or charges incurred prior to termination.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>c. Effect of Termination:</strong> Upon any termination of
          these Terms for any reason: (i) all rights and licenses granted to you
          under these Terms will terminate immediately; (ii) you must cease all
          use of the Platform and any of our content; and (iii) you will no
          longer have access to your account or any User Content you have posted
          (the Company may, however, retain copies of your User Content and data
          as needed for archival or legal purposes).{" "}
          <strong>
            Sections of these Terms which by their nature should survive
            termination (including, but not limited to, provisions regarding
            ownership of intellectual property, indemnification, warranty
            disclaimers, limitation of liability, and governing law) shall
            survive termination
          </strong>{" "}
          and remain in full force and effect.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          The Company shall not be liable to you or any third party for account
          termination or access restriction in accordance with these Terms. If
          your account is terminated or suspended by the Company, you agree not
          to create or use another account to access the Platform without our
          express permission.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          In addition to other grounds, accounts may be terminated or suspended
          if:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            You fail to pass KYC/AML verification;
          </li>
          <li className={classes.listItem}>
            You are reasonably suspected of using the Platform to launder money,
            commit fraud, or finance prohibited activities;
          </li>
          <li className={classes.listItem}>
            Transactions are flagged under the Company's AML/Anti-Fraud
            protocols.
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          13. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>
            THE PLATFORM AND ALL SERVICES, CONTENT, FEATURES, AND MATERIALS
            PROVIDED THROUGH IT ARE OFFERED ON AN "AS IS" AND "AS AVAILABLE"
            BASIS
          </strong>{" "}
          without any warranties of any kind, either express or implied. To the
          maximum extent permitted under applicable law,{" "}
          <strong>
            TOPV1 LLC expressly disclaims all warranties, express, implied, or
            statutory,
          </strong>{" "}
          including, but not limited to, implied warranties of merchantability,
          fitness for a particular purpose, title, non-infringement, and any
          warranties arising out of course of dealing or usage of trade.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Without limiting the generality of the foregoing, the Company makes{" "}
          <strong>no representations or warranties</strong> that the Platform
          will be accurate, reliable, uninterrupted, secure, or error-free; that
          defects will be corrected; or that the Service or the server(s) that
          makes it available are free of viruses or other harmful components. We
          cannot guarantee and do not promise any specific results from the use
          of the Platform.{" "}
          <strong>
            Your use of the Platform is at your own discretion and risk.
          </strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You understand that the{" "}
          <strong>
            Company is not responsible for the content or conduct of any user
            (including any Creator or Fan),
          </strong>{" "}
          and you may be exposed to content that is inaccurate, objectionable,
          or otherwise unsuited to your purpose. We make no warranty regarding
          the quality, suitability, or legality of any content or services
          obtained through the Platform. The Company does not guarantee the
          continuous, uninterrupted, or secure access to the Platform, which may
          be affected by factors outside of our control.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Some jurisdictions do not allow the exclusion of certain warranties.{" "}
          <strong>
            If any legally required warranty cannot be disclaimed under
            applicable law, then to the extent permitted by law, the scope and
            duration of such warranty will be the minimum necessary to comply
            with such requirement.
          </strong>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          14. Limitation of Liability
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          To the fullest extent permitted by applicable law,{" "}
          <strong>
            in no event shall TOPV1 LLC or its owners, directors, officers,
            employees, agents, affiliates, licensors, or service providers be
            liable
          </strong>{" "}
          for any indirect, incidental, consequential, special, exemplary, or
          punitive damages whatsoever, including but not limited to damages for
          loss of profits, loss of business or revenue, loss of data, loss of
          goodwill, service interruption, computer damage, system failure, the
          cost of substitute services, or any other intangible losses arising
          out of or in connection with: (a) your access to or use of (or
          inability to access or use) the Platform; (b) any conduct or content
          of any user or third party on the Platform, including defamatory,
          offensive, or illegal conduct of other users or third parties; (c) any
          content obtained from the Platform; or (d) the unauthorized access,
          use, or alteration of your transmissions or content,{" "}
          <strong>even if</strong> the Company has been advised of the
          possibility of such damages.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>
            To the extent any liability is not lawfully disclaimed,
          </strong>{" "}
          in no event will the Company's total cumulative liability to you for
          all claims arising out of or relating to these Terms or the use of the
          Platform <strong>exceed the greater of:</strong> (i) the total amount
          (if any) you have paid to TOPV1 LLC in the twelve (12) months
          immediately preceding the events giving rise to the claim; or (ii){" "}
          <strong>USD $100.00.</strong> The existence of multiple claims will
          not enlarge this limit. This limitation of liability applies to the
          fullest extent permitted by law in the applicable jurisdiction.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Some jurisdictions do not allow the exclusion or limitation of certain
          liabilities (such as incidental or consequential damages), so some of
          the above limitations may not apply to you. In such jurisdictions, the
          liability of the Company and its affiliates will be limited to the
          maximum extent permitted by law.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You acknowledge and agree that the Company has offered the Platform
          and set its fees (if any) in reliance on the disclaimers of warranty
          and limitations of liability set forth herein, which reflect a
          reasonable and fair allocation of risk between the parties and form an
          essential basis of the bargain between you and the Company.{" "}
          <strong>
            You agree that the limitations and exclusions of liability and
            warranty in these Terms will survive and apply even if found to have
            failed of their essential purpose.
          </strong>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          15. Indemnification
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          You agree to{" "}
          <strong>indemnify, defend, and hold harmless TOPV1 LLC,</strong> its
          parent, subsidiaries, and affiliates, and their respective officers,
          directors, managers, members, employees, contractors, agents,
          licensors, and representatives (collectively, the{" "}
          <strong>"Indemnified Parties"</strong>) from and against any and all
          claims, demands, actions, suits, proceedings, losses, liabilities,
          damages, judgments, settlements, costs, and expenses (including
          reasonable attorneys' fees and court costs) arising out of or relating
          to:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>Your Use of the Platform:</strong> Your access to or use of
            the Platform, including any content or information you obtain from
            the Service;
          </li>
          <li className={classes.listItem}>
            <strong>User Content:</strong> Your submission, posting, or
            transmission of User Content, including any actual or alleged
            infringement, misappropriation, or violation of any third party's
            rights by such content;
          </li>
          <li className={classes.listItem}>
            <strong>Violation of Terms:</strong> Your breach or alleged breach
            of these Terms or of any representation, warranty, or agreement
            referenced herein;
          </li>
          <li className={classes.listItem}>
            <strong>Violation of Law or Rights:</strong> Your violation of any
            applicable law, rule, or regulation, or the rights (including
            intellectual property, privacy, publicity or other rights) of any
            other person or entity.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          The Company reserves the right, at your expense, to assume the
          exclusive defense and control of any matter otherwise subject to
          indemnification by you (in which case you agree to cooperate with the
          Company in asserting any available defenses). You agree not to settle
          any such matter without the prior written consent of the Company.{" "}
          <strong>
            This indemnification obligation will survive any termination of your
            account or of these Terms.
          </strong>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          16. Governing Law and Jurisdiction
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          These Terms and any dispute or claim (including non-contractual
          disputes or claims) arising out of or relating to the Platform or
          these Terms shall be{" "}
          <strong>
            governed by and construed in accordance with the laws of the State
            of Nevada, USA,
          </strong>{" "}
          without giving effect to any conflict of laws principles that would
          result in the application of the laws of another jurisdiction. The
          United Nations Convention on Contracts for the International Sale of
          Goods does not apply to these Terms.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Jurisdiction and Venue:</strong> You agree that any legal
          action or proceeding arising out of or relating to these Terms or your
          use of the Platform shall be brought exclusively in the state or
          federal courts located within Clark County, Nevada, USA,{" "}
          <strong>
            and you hereby irrevocably consent and submit to the personal
            jurisdiction
          </strong>{" "}
          of such courts for the purposes of litigating any such action. You
          waive any and all objections to the exercise of jurisdiction over you
          by such courts and to venue in such courts, including any argument
          that such forum is inconvenient.
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you are using the Platform from a location outside the United
          States, you are solely responsible for compliance with all local laws.
          We make no representation that the Platform is appropriate or
          available for use in any particular jurisdiction.{" "}
          <strong>
            Access to the Platform from territories where its content or
            operation is illegal is prohibited.
          </strong>{" "}
          You agree that you will not use the Platform in any country or in any
          manner prohibited by U.S. export laws, restrictions, or regulations.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          17. Miscellaneous Provisions
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            <strong>Entire Agreement:</strong> These Terms (including any
            incorporated policies such as our Privacy Policy and any additional
            terms or agreements for specific services or features that we may
            provide) constitute the <strong>entire agreement</strong> between
            you and TOPV1 LLC regarding your use of the Platform. They supersede
            all prior and contemporaneous understandings, agreements,
            representations, and warranties, both written and oral, regarding
            the Platform. You acknowledge that you have not relied on any
            representation, warranty, or statement not expressly set out in
            these Terms.
          </li>
          <li className={classes.listItem}>
            <strong>Severability:</strong> If any provision of these Terms is
            held to be invalid, illegal, or unenforceable by a court or other
            tribunal of competent jurisdiction, that provision shall be
            eliminated or limited{" "}
            <strong>to the minimum extent necessary</strong> so that the
            remaining provisions of the Terms will continue in full force and
            effect. Any invalid or unenforceable provision will be interpreted
            to fulfill its intended purpose to the maximum extent permitted by
            law, and the remaining provisions will remain in effect.
          </li>
          <li className={classes.listItem}>
            <strong>No Waiver:</strong> <strong>No waiver</strong> of any term
            or condition of these Terms by the Company shall be deemed a further
            or continuing waiver of such term or any other term, and the
            Company's failure to assert any right or provision under these Terms
            shall not constitute a waiver of such right or provision.{" "}
            <strong>Any waiver</strong> of any provision of these Terms will be
            effective only if in writing and signed by an authorized
            representative of TOPV1 LLC.
          </li>
          <li className={classes.listItem}>
            <strong>Assignment:</strong> You may{" "}
            <strong>not assign or transfer</strong> these Terms (by operation of
            law or otherwise) without the prior written consent of the Company.
            Any attempt by you to assign or transfer these Terms, without such
            consent, will be null and void. The Company may freely assign or
            transfer its rights and obligations under these Terms to any third
            party as it deems appropriate (for example, in the event of a
            merger, acquisition, reorganization, or sale of assets, or by
            operation of law or otherwise). These Terms shall be binding upon
            and inure to the benefit of the parties and their permitted
            successors and assigns.
          </li>
          <li className={classes.listItem}>
            <strong>No Agency:</strong> No joint venture, partnership,
            employment, or agency relationship exists between you and the
            Company as a result of these Terms or your use of the Platform. You
            do not have any authority of any kind to bind the Company in any
            respect whatsoever.
          </li>
          <li className={classes.listItem}>
            <strong>Force Majeure:</strong> The Company shall not be liable for
            any delay or failure in performance resulting from causes outside
            its reasonable control, including but not limited to acts of God,
            natural disasters, war, terrorism, riots, embargoes, pandemic, acts
            of civil or military authorities, fire, floods, accidents, network
            infrastructure failures, strikes, or shortages of transportation
            facilities, fuel, energy, labor, or materials. In such event, the
            Company shall be excused from further performance to the extent of
            the disruption.
          </li>
          <li className={classes.listItem}>
            <strong>Notices:</strong> The Company may provide notifications
            (whether required by law or for marketing or other business
            purposes) to you via email to the address associated with your
            account, through posting of such notice on the Platform, or through
            other reasonable means. You are responsible for keeping your contact
            information up-to-date. All legal notices to the Company (such as
            formal notices of dispute or legal claims) must be in writing and
            mailed to the Company's registered business address or otherwise
            delivered to the Company in a manner that provides proof of
            delivery.
          </li>
          <li className={classes.listItem}>
            <strong>Headings and Interpretation:</strong> The section titles and
            headings in these Terms are for convenience only and have no legal
            or contractual effect. In these Terms, the words{" "}
            <strong>"including"</strong> and <strong>"include"</strong> mean
            "including, but not limited to." Any ambiguity in these Terms shall
            not be construed against the drafter.
          </li>
          <li className={classes.listItem}>
            <strong>Language:</strong> These Terms and any related documents or
            communications are in the English language. If these Terms are
            translated into another language, the English version will prevail
            in the event of any conflict or discrepancy in interpretation.
          </li>
          <li className={classes.listItem}>
            <strong>AML/CTF Compliance:</strong> The Company maintains and
            enforces an internal <strong>AML/Anti-Fraud Policy</strong>{" "}
            consistent with the U.S. Bank Secrecy Act, FinCEN guidelines, FATF
            recommendations, and applicable international standards. Users
            acknowledge that their activity on the Platform may be monitored,
            reviewed, and reported to financial institutions or regulators in
            accordance with these laws.
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          18. Contact Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you have any questions, concerns, or comments about these Terms or
          the Platform, or need to provide any notice to the Company pursuant to
          these Terms, you may contact us using the information below:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>TOPV1 LLC (d/b/a "Sugar Cribs")</strong>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            Email:{" "}
            <a href="mailto:support@sugarcribs.com" className={classes.link}>
              support@sugarcribs.com
            </a>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            732 S. 6th Street, Suite N, Las Vegas, NV 89101, USA (Registered
            Office)
          </Typography>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          We will endeavor to respond to all legitimate inquiries in a timely
          manner.
        </Typography>
      </Container>
    </>
  );
}
